(() => {
  'use strict';
  if (window.StudioCollectionKeychain) return;
  const ns = 'http://www.w3.org/2000/svg';
  let serial = 0;
  const node = (name, attrs = {}, parent) => {
    const element = document.createElementNS(ns, name);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value);
    if (parent) parent.appendChild(element);
    return element;
  };
  const smooth = value => { const p = Math.max(0, Math.min(1, value)); return p * p * (3 - 2 * p); };
  const rotate = (x, y, angle) => {
    const radians = angle * Math.PI / 180;
    return { x: x * Math.cos(radians) - y * Math.sin(radians), y: x * Math.sin(radians) + y * Math.cos(radians) };
  };
  function create(section) {
    const rig = section.querySelector('.at-nfc-scan__phone-rig');
    const tags = [section.querySelector('.studio-phone-collect-tag--left'), section.querySelector('.at-nfc-scan__tag'), section.querySelector('.studio-phone-collect-tag--right')];
    if (!rig || tags.some(tag => !tag)) return null;
    const id = 'CollectionMetal-' + (++serial);
    const layers = ['back', 'front'].map(layer => node('svg', {
      class: 'studio-collection-keychain studio-collection-keychain--' + layer,
      viewBox: '0 0 1000 1000', 'aria-hidden': 'true', focusable: 'false'
    }, rig));
    const [back, front] = layers;
    const defs = node('defs', {}, back);
    const gradient = node('linearGradient', { id, x1: '0%', y1: '0%', x2: '100%', y2: '100%' }, defs);
    ['#606873', '#ffffff', '#a5acb2', '#eff2f4', '#676f7a', '#ffffff', '#9299a1'].forEach((color, i) => node('stop', { offset: i / 6, 'stop-color': color }, gradient));
    const metal = 'url(#' + id + ')';
    const chains = tags.map((tag, index) => {
      const group = node('g', { 'data-keychain-chain': index }, back);
      const links = Array.from({ length: 40 }, (_, j) => {
        const link = node('g', {}, group), rx = j % 2 ? 4.6 : 8.3;
        node('ellipse', { rx, ry: 12.5, fill: 'none', stroke: '#565c65', 'stroke-width': 4.4 }, link);
        node('ellipse', { rx, ry: 12.5, fill: 'none', stroke: metal, 'stroke-width': 3.1 }, link);
        node('path', { d: `M ${-rx} 1 C ${-rx} -8 -3 -13 1 -12`, fill: 'none', stroke: '#fff', 'stroke-width': 1, opacity: .86 }, link);
        return link;
      });
      const terminal = node('g', {}, back);
      node('ellipse', { rx: 10, ry: 22, fill: 'none', stroke: '#666d77', 'stroke-width': 5.2 }, terminal);
      node('ellipse', { rx: 10, ry: 22, fill: 'none', stroke: metal, 'stroke-width': 3.7 }, terminal);
      const lip = node('g', { 'data-keychain-lug': index }, front);
      node('path', { d: 'M -10 0 A 10 22 0 0 0 10 0', fill: 'none', stroke: '#737983', 'stroke-width': 5.2 }, lip);
      node('path', { d: 'M -10 0 A 10 22 0 0 0 10 0', fill: 'none', stroke: metal, 'stroke-width': 3.7 }, lip);
      return { tag, links, terminal, lip };
    });
    const ring = node('g', { 'data-keychain-ring': '' }, back);
    node('circle', { r: 87, fill: 'none', stroke: '#555e69', 'stroke-width': 14 }, ring);
    node('circle', { r: 87, fill: 'none', stroke: metal, 'stroke-width': 11 }, ring);
    node('circle', { cx: -2, cy: 2, r: 80, fill: 'none', stroke: '#8c949f', 'stroke-width': 5.4 }, ring);
    node('circle', { cx: -2, cy: 2, r: 80, fill: 'none', stroke: metal, 'stroke-width': 3.4 }, ring);
    node('path', { d: 'M -84 -20 A 87 87 0 0 1 40 -77', fill: 'none', stroke: '#fff', 'stroke-width': 2.1, 'stroke-linecap': 'round' }, ring);
    node('path', { d: 'M 61 62 A 87 87 0 0 1 -8 87', fill: 'none', stroke: '#fff', 'stroke-width': 1.6, opacity: .9 }, ring);
    let geometry = null, last = null, resizeFrame = 0, destroyed = false;
    function measure() {
      const width = parseFloat(getComputedStyle(rig).width);
      if (!width) return;
      const scale = 1000 / width;
      geometry = tags.map(tag => {
        const css = getComputedStyle(tag), origin = css.transformOrigin.split(' ').map(parseFloat);
        return {
          x: (parseFloat(css.left) || 0) * scale, y: (parseFloat(css.top) || 0) * scale,
          w: parseFloat(css.width) * scale, h: parseFloat(css.height) * scale,
          ox: origin[0] * scale, oy: origin[1] * scale
        };
      });
    }
    function anchor(g, pose) {
      // Literal lug center from the front asset's atlas crop (252 18 490 468).
      const delta = rotate(g.w * (252 / 490) - g.ox, g.h * (36 / 468) - g.oy, pose.angle);
      return { x: g.x + g.ox + delta.x + pose.x / 100 * g.w, y: g.y + g.oy + delta.y + pose.y / 100 * g.h };
    }
    function paint(p, poses) {
      if (destroyed) return;
      last = [p, poses];
      const joined = smooth((p - .88) / .12);
      for (const layer of layers) layer.style.opacity = joined.toFixed(3);
      if (!joined) return;
      if (!geometry) measure();
      if (!geometry) return;
      const anchors = geometry.map((g, i) => anchor(g, poses[i]));
      const hub = { x: anchors[1].x, y: Math.min(...anchors.map(a => a.y)) - 240 - (1 - joined) * 70 };
      ring.setAttribute('transform', `translate(${hub.x} ${hub.y})`);
      anchors.forEach((end, i) => {
        const start = { x: hub.x + (i - 1) * 48, y: hub.y + (i === 1 ? 88 : 72) };
        const axisLength = Math.hypot(end.x - start.x, end.y - start.y);
        const ux = (end.x - start.x) / axisLength, uy = (end.y - start.y) / axisLength;
        const finish = { x: end.x - ux * 30, y: end.y - uy * 30 };
        const control = { x: (start.x + finish.x) / 2, y: (start.y + finish.y) / 2 + (i === 1 ? 0 : 28) };
        const length = Math.hypot(finish.x - start.x, finish.y - start.y);
        const count = Math.max(2, Math.min(40, Math.ceil(length / 17)));
        chains[i].links.forEach((link, j) => {
          link.style.display = j < count ? '' : 'none';
          if (j >= count) return;
          const t = j / (count - 1), u = 1 - t;
          const x = u*u*start.x + 2*u*t*control.x + t*t*finish.x;
          const y = u*u*start.y + 2*u*t*control.y + t*t*finish.y;
          const dx = 2*u*(control.x-start.x) + 2*t*(finish.x-control.x), dy = 2*u*(control.y-start.y) + 2*t*(finish.y-control.y);
          link.setAttribute('transform', `translate(${x} ${y}) rotate(${Math.atan2(dy, dx)*180/Math.PI-90})`);
        });
        const terminalTransform = `translate(${end.x-ux*12} ${end.y-uy*12}) rotate(${Math.atan2(uy, ux)*180/Math.PI-90})`;
        chains[i].terminal.setAttribute('transform', terminalTransform);
        chains[i].lip.setAttribute('transform', terminalTransform);
        chains[i].lip.dataset.anchorX = end.x.toFixed(4);
        chains[i].lip.dataset.anchorY = end.y.toFixed(4);
      });
    }
    const resize = new ResizeObserver(() => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => { if (destroyed) return; measure(); if (last) paint(...last); });
    });
    resize.observe(rig); tags.forEach(tag => resize.observe(tag));
    measure();
    return { paint, destroy() { destroyed = true; cancelAnimationFrame(resizeFrame); resize.disconnect(); layers.forEach(layer => layer.remove()); } };
  }
  window.StudioCollectionKeychain = { create };
})();
